import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SectionTitle from './SectionTitle';

const LinkedInBrandIcon = (props) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <rect width="24" height="24" rx="3" fill="#0A66C2" />
    <path
      fill="#FFFFFF"
      d="M7.2 9.6h2.7v8.7H7.2V9.6zm1.35-4.2a1.57 1.57 0 1 1 0 3.14 1.57 1.57 0 0 1 0-3.14zM11.55 9.6h2.6v1.19h.04c.36-.68 1.25-1.4 2.57-1.4 2.75 0 3.26 1.81 3.26 4.17v4.74h-2.7v-4.2c0-1 0-2.3-1.4-2.3-1.4 0-1.62 1.1-1.62 2.23v4.27h-2.7V9.6z"
    />
  </svg>
);

const AppStoreIcon = (props) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor" {...props}>
    <path d="M17.05 12.54a4.36 4.36 0 0 1 2.08-3.66 4.47 4.47 0 0 0-3.52-1.9c-1.48-.16-2.9.88-3.66.88-.77 0-1.93-.86-3.18-.84a4.7 4.7 0 0 0-3.95 2.41c-1.7 2.95-.44 7.3 1.21 9.69.83 1.17 1.8 2.48 3.07 2.43 1.24-.05 1.7-.79 3.19-.79 1.48 0 1.91.79 3.21.76 1.33-.02 2.17-1.18 2.98-2.36a10.4 10.4 0 0 0 1.36-2.79 4.22 4.22 0 0 1-2.79-3.83zM14.6 5.43a4.27 4.27 0 0 0 .98-3.1 4.37 4.37 0 0 0-2.85 1.48 4.07 4.07 0 0 0-1 3 3.62 3.62 0 0 0 2.87-1.38z" />
  </svg>
);

const PlayStoreIcon = (props) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <path fill="#34A853" d="M3.6 2.8C3.22 3.1 3 3.6 3 4.25v15.5c0 .65.22 1.15.6 1.45l8.4-8.4-8.4-8z" />
    <path fill="#FBBC04" d="M16.4 9.6 13.1 11.5l-1.1 1.3 1.1 1.3 3.3 1.9 3.5-2c.7-.4 1.1-.95 1.1-1.5 0-.55-.4-1.1-1.1-1.5l-3.5-1.4z" />
    <path fill="#EA4335" d="M3.6 21.2c.4.3.95.3 1.55-.05L16.4 14.4l-3.3-2.9-9.5 9.7z" />
    <path fill="#4285F4" d="M3.6 2.8 13.1 11.5l3.3-2.9L5.15 2.55C4.55 2.2 4 2.5 3.6 2.8z" />
  </svg>
);

const Section = styled.section`
  padding: 140px 56px 120px;
  background: ${({ theme }) => theme.colors.bg};
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;

  @media (max-width: 768px) {
    padding: 90px 20px 80px;
  }
`;

const Eyebrow = styled.div`
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #FF8A6B;
  text-align: center;
  margin-bottom: 18px;
`;

const Quotes = styled.div`
  max-width: 1280px;
  margin: 24px auto 0;
  display: flex;
  flex-direction: column;
  gap: 96px;

  @media (max-width: 768px) {
    gap: 64px;
  }
`;

const QuoteRow = styled(motion.figure)`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 48px;
  align-items: start;
  margin: 0;

  &:nth-child(even) {
    grid-template-columns: 1fr 200px;
    .meta { order: 2; text-align: right; align-items: flex-end; }
    blockquote { order: 1; }
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 24px;

    &:nth-child(even) {
      grid-template-columns: 1fr;
      .meta { order: 1; text-align: left; align-items: flex-start; }
      blockquote { order: 2; }
    }
  }
`;

const Meta = styled.figcaption.attrs({ className: 'meta' })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.14)`};
`;

const Name = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Role = styled.div`
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Location = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.75;
  margin-top: 2px;

  svg { width: 11px; height: 11px; flex-shrink: 0; }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LinkedInLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  overflow: hidden;
  opacity: 0.9;
  transition: opacity 200ms ease, transform 200ms ease;

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
  }

  svg { width: 22px; height: 22px; display: block; }
`;

const Company = styled.a`
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: #FF8A6B;
  text-decoration: none;
  margin-top: 6px;
  border-bottom: 1px solid transparent;
  transition: border-color 200ms ease;

  &:hover { border-bottom-color: #FF8A6B; }
`;

const StoreLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const StoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.85;
  transition: opacity 200ms ease, transform 200ms ease, color 200ms ease;

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.colors.text.primary};
    transform: translateY(-1px);
  }

  svg { width: 18px; height: 18px; display: block; }
`;

const Quote = styled.blockquote`
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 400;
  font-size: clamp(24px, 2.6vw, 34px);
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  position: relative;

  &::before {
    content: '\\201C';
    position: absolute;
    top: -28px;
    left: -8px;
    font-size: 80px;
    line-height: 1;
    color: #FF5B3A;
    opacity: 0.5;
    font-family: 'Fraunces', serif;
  }
`;

const Accent = styled.span`
  color: #FF5B3A;
`;

const testimonials = [
  {
    parts: [
      'Real engineering, accelerated by ',
      { accent: 'AI' },
      '. They delivered in ',
      { accent: 'two months' },
      ' what others would take ',
      { accent: 'a year' },
      '.',
    ],
    name: 'Dimitar Popovski',
    role: 'Founder',
    company: 'Shakeout Collective',
    companyUrl: 'https://www.shakeoutco.com/',
    location: 'Toronto, Canada',
    linkedinUrl: 'https://www.linkedin.com/in/dimitar-popovski-b16aa02/',
    appStoreUrl: 'https://apps.apple.com/ca/app/shakeout-collective/id6760942288',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.shakeout.prod',
  },
  {
    parts: [
      'What sets them apart is the mindset, they take ',
      { accent: 'ownership' },
      ' and ',
      { accent: 'think' },
      ' like ',
      { accent: 'partners' },
      ', not contractors ticking off tasks. Communication is ',
      { accent: 'sharp' },
      ' and ',
      { accent: 'proactive' },
      ', so it feels less like outsourcing and more like an extension of your ',
      { accent: 'team' },
      '.',
    ],
    name: 'Tjaco Walvis',
    role: 'Founder',
    company: 'Sokrateque.AI',
    companyUrl: 'https://www.sokrateque.ai/',
    location: 'Amsterdam, Netherlands',
    linkedinUrl: 'https://www.linkedin.com/in/tjaco-walvis-a154212/',
  },
];

const rowVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

const renderQuote = (parts) =>
  parts.map((part, i) =>
    typeof part === 'string' ? (
      <React.Fragment key={i}>{part}</React.Fragment>
    ) : (
      <Accent key={i}>{part.accent}</Accent>
    )
  );

const InTheirWords = () => (
  <Section id="in-their-words">
    <Eyebrow>What founders say</Eyebrow>
    <SectionTitle>In their words</SectionTitle>
    <Quotes>
      {testimonials.map((t, i) => (
        <QuoteRow
          key={i}
          variants={rowVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <Meta>
            <NameRow>
              <Name>{t.name}</Name>
              {t.linkedinUrl && (
                <LinkedInLink
                  href={t.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${t.name} on LinkedIn`}
                >
                  <LinkedInBrandIcon />
                </LinkedInLink>
              )}
            </NameRow>
            <Role>{t.role}</Role>
            {t.location && (
              <Location>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {t.location}
              </Location>
            )}
            {t.company && (
              <Company href={t.companyUrl || '#'} target="_blank" rel="noreferrer">
                {t.company}
              </Company>
            )}
            {(t.appStoreUrl || t.playStoreUrl) && (
              <StoreLinks>
                {t.appStoreUrl && (
                  <StoreLink
                    href={t.appStoreUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${t.company} on the App Store`}
                  >
                    <AppStoreIcon />
                  </StoreLink>
                )}
                {t.playStoreUrl && (
                  <StoreLink
                    href={t.playStoreUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${t.company} on Google Play`}
                  >
                    <PlayStoreIcon />
                  </StoreLink>
                )}
              </StoreLinks>
            )}
          </Meta>
          <Quote>{renderQuote(t.parts)}</Quote>
        </QuoteRow>
      ))}
    </Quotes>
  </Section>
);

export default InTheirWords;
